import { useCallback, useEffect, useMemo, useState } from 'react';
import { Player as YlePlayer } from '@yleisradio/player-types';
import { PlaybackRateSetter } from 'components/Player/AudioPlayerWrapper/typesAudioPlayerWrapper';
import logger from 'services/logger';

export function useIsExpandedPlayerAvailable(): boolean {
  const [state, setState] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia('screen and (min-width: 800px)');

    setState(!mediaQuery.matches);

    const onChange = (e: MediaQueryListEvent) => {
      setState(!e.matches);
    };

    mediaQuery.addEventListener('change', onChange);

    return () => {
      mediaQuery.removeEventListener('change', onChange);
    };
  }, []);

  return state;
}

export const useAudioPlayerProgression = (playerInstance: YlePlayer | null) => {
  const [progression, setProgression] = useState(0);

  const audioSeek = useCallback(
    (position: number) => {
      if (!playerInstance)
        throw new Error("Can't seek because playerInstance is undefined");

      playerInstance.seek(position);
      setProgression(position);
    },
    [playerInstance]
  );

  const handleOnTimeUpdate = useCallback((time: number) => {
    setProgression(time);
  }, []);

  return { progression, audioSeek, handleOnTimeUpdate };
};

export const useAudioPlayerVolume = (
  playerInstance: YlePlayer | null
): {
  isMuted: boolean;
  setMute: ((mute: boolean) => void) | null;
  volume: number;
  setVolume: ((volume: number) => void) | null;
} => {
  const [muteState, setMuteState] = useState(false);
  const [volumeState, setVolumeState] = useState(1);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    try {
      const storageValue = window.localStorage.getItem('PlayerUI:settings');

      if (!storageValue) return;

      const playerSettings = JSON.parse(storageValue);

      if (
        typeof playerSettings.volume == 'number' &&
        playerSettings.volume >= 0 &&
        playerSettings.volume <= 1
      ) {
        setVolumeState(playerSettings.volume);
      }
      if (playerSettings.muted || playerSettings.volume === 0) {
        setMuteState(true);
      }
    } catch (error) {
      logger.error(
        error,
        'Cannot read playerUI volume value from local storage'
      );
    }
  }, []);

  const setMute = useCallback(
    (mute: boolean): void => {
      if (!playerInstance)
        throw new Error(
          "Can't set mute state because playerInstance is undefined"
        );

      setMuteState(mute);

      if (mute) {
        playerInstance.mute();
      } else {
        playerInstance.unmute();
      }
    },
    [playerInstance]
  );

  const setVolume = useCallback(
    (volume: number): void => {
      if (!playerInstance)
        throw new Error("Can't set volume because playerInstance is undefined");

      setVolumeState(volume);
      playerInstance.volume(volume);
    },
    [playerInstance]
  );

  return {
    isMuted: muteState,
    setMute: playerInstance ? setMute : null,
    volume: volumeState,
    setVolume: playerInstance ? setVolume : null,
  };
};

export const usePlaybackRate = (playerInstance: YlePlayer | null) => {
  const [currentPlaybackRate, setCurrentPlaybackRate] = useState<number>(1);

  const setPlaybackRate: PlaybackRateSetter = useCallback(
    (rate: number) => {
      if (!playerInstance) {
        throw new Error(
          "Can't set playback rate because playerInstance is undefined"
        );
      }

      playerInstance.setPlaybackRate(rate);
      setCurrentPlaybackRate(rate);
    },
    [playerInstance]
  );

  const playbackRates = useMemo(() => {
    if (!playerInstance) return [];

    return playerInstance.playbackRates();
  }, [playerInstance]);

  return {
    currentPlaybackRate,
    playbackRates,
    setPlaybackRate,
  };
};
